import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  SaveVideoCall,
  DeleteVideoUrl,
} from "../../Redux/actions/videoCallActions";
import { useDispatch, useSelector } from "react-redux";
import Sharemedicalrecords from "./Sharemedicalrecords";
import { APP_ROUTES } from "../../PageRouting/pageRoutes";
import AppointmentService from "../../Services/AppointmentService";
import { Flex, Spin, Alert, Modal } from "antd";
import { deleteCallRecord } from "../../config/firebase";
import {
  clearSelectedAppt,
  setSelectedAppt,
} from "../../Redux/actions/appointmentActions";
import { decryptData } from "../../utils/valueDecrypt";

const VideoCall = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [url, setUrl] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [mediaState, setMediaState] = useState("checking");
  const [mediaError, setMediaError] = useState("");
  const [showPermissionModal, setShowPermissionModal] = useState(false);

  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [iframeError, setIframeError] = useState(false);

  const permissionStreamRef = useRef(null);

  const { selectedAppt } = useSelector((state) => state.appt);
  const { loggedUser } = useSelector((state) => state.auth);

  const selectedPatient = location?.state?.selectedAppDetails;
  const patientId = loggedUser?.patientId;

  useEffect(() => {
    if (selectedAppt !== null) return;

    if (selectedPatient) {
      dispatch(setSelectedAppt(selectedPatient));
      return;
    }

    try {
      const raw = sessionStorage.getItem("selectedAppt");

      if (raw) {
        dispatch(setSelectedAppt(JSON.parse(raw)));
      }
    } catch (e) {
      console.error("Failed to parse selectedAppt from sessionStorage", e);
    }
  }, [selectedPatient, selectedAppt, dispatch]);

  const isMediaSupported = useCallback(() => {
    return (
      typeof window !== "undefined" &&
      window.isSecureContext &&
      !!navigator.mediaDevices &&
      typeof navigator.mediaDevices.getUserMedia === "function"
    );
  }, []);

  const stopPermissionStream = useCallback(() => {
    if (!permissionStreamRef.current) return;

    permissionStreamRef.current.getTracks().forEach((track) => {
      try {
        track.stop();
      } catch (e) {
        console.warn("Failed to stop media track:", e);
      }
    });

    permissionStreamRef.current = null;
  }, []);

  const getPermissionState = useCallback(async () => {
    if (!navigator.permissions?.query) {
      return {
        camera: "unknown",
        microphone: "unknown",
      };
    }

    let camera = "unknown";
    let microphone = "unknown";

    try {
      const result = await navigator.permissions.query({
        name: "camera",
      });
      camera = result.state;
    } catch (e) {
      console.debug("Camera permission query unsupported:", e);
    }

    try {
      const result = await navigator.permissions.query({
        name: "microphone",
      });
      microphone = result.state;
    } catch (e) {
      console.debug("Microphone permission query unsupported:", e);
    }

    return { camera, microphone };
  }, []);

  const requestMediaPermissions = useCallback(async () => {
    setMediaError("");

    if (!isMediaSupported()) {
      setMediaState("unsupported");

      setMediaError(
        "This browser does not support camera and microphone access, " +
          "or the page is not running over HTTPS.",
      );

      return false;
    }

    try {
      setMediaState("requesting");

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "user",
        },
        audio: true,
      });

      permissionStreamRef.current = stream;

      setMediaState("granted");
      setMediaError("");

      stopPermissionStream();

      return true;
    } catch (error) {
      console.error("Camera/microphone permission error:", error);

      stopPermissionStream();

      const errorName = error?.name;

      if (
        errorName === "NotAllowedError" ||
        errorName === "PermissionDeniedError"
      ) {
        setMediaState("denied");

        setMediaError(
          "Camera or microphone access is blocked for this site. " +
            "Allow Camera and Microphone in your browser site settings, " +
            "then press Retry.",
        );

        setShowPermissionModal(true);

        return false;
      }

      if (errorName === "NotFoundError") {
        setMediaState("not-found");
        setMediaError("No camera or microphone was found on this device.");
        return false;
      }

      if (errorName === "NotReadableError") {
        setMediaState("busy");
        setMediaError(
          "The camera or microphone is already being used by another app or tab.",
        );
        return false;
      }

      if (errorName === "SecurityError") {
        setMediaState("blocked");
        setMediaError(
          "The browser blocked access to your camera or microphone.",
        );
        return false;
      }

      setMediaState("error");
      setMediaError(
        `Unable to access camera/microphone${
          error?.message ? `: ${error.message}` : "."
        }`,
      );

      return false;
    }
  }, [isMediaSupported, stopPermissionStream]);

  const checkMediaPermissions = useCallback(async () => {
    if (!isMediaSupported()) {
      setMediaState("unsupported");
      return;
    }

    const permissions = await getPermissionState();

    console.log("Media permission state:", permissions);

    if (
      permissions.camera === "granted" &&
      permissions.microphone === "granted"
    ) {
      setMediaState("granted");
      return;
    }

    if (
      permissions.camera === "denied" ||
      permissions.microphone === "denied"
    ) {
      setMediaState("denied");

      setMediaError(
        "Camera and microphone are blocked for this site. " +
          "Allow them in browser site settings.",
      );

      return;
    }

    setMediaState("prompt");
  }, [getPermissionState, isMediaSupported]);

  useEffect(() => {
    checkMediaPermissions();
  }, [checkMediaPermissions]);

  const getLink = useCallback(async () => {
    const formattedDate = new Date()
      .toISOString()
      .slice(0, 19)
      .replace("T", " ");

    const payload = {
      createdDate: formattedDate,
      merchantId: "hslA/69EPSD7qXTYAqWxuCqnq8KLo8Q3AFgvFVKsIBk=",
      merchantUserId: "HELIXO",
      modifiedDate: formattedDate,
      saltkey: "ojKdN1zB/MdTtJ1hBoIzVtRK60oyos5x29koJaadxL8=",
      uniqueId: selectedPatient?.appointmentId,
      userName: loggedUser?.firstName || "Patient",
    };

    try {
      const response = await axios.post(
        "https://api.v-connect.helixotech.com/HelixoConnect/join-call",
        payload,
      );

      const decryptedString = decryptData(response.data.data);

      const decryptedJson =
        typeof decryptedString === "string"
          ? JSON.parse(decryptedString)
          : decryptedString;

      if (decryptedJson?.url) {
        setUrl(decryptedJson.url);
        setIframeLoaded(false);
        setIframeError(false);
      } else {
        throw new Error("Video call URL was not returned");
      }
    } catch (err) {
      console.error("Video link error:", err);

      setIframeError(true);
      setMediaError("Unable to create the video call. Please try again.");
    }
  }, [loggedUser?.firstName, selectedPatient?.appointmentId]);

  useEffect(() => {
    if (mediaState !== "granted") return;
    if (!selectedPatient?.appointmentId) return;

    getLink();
  }, [mediaState, selectedPatient?.appointmentId, getLink]);

  useEffect(() => {
    if (!url) return;

    dispatch(
      SaveVideoCall({
        callurl: url,
        callType: true,
        apptDetails: selectedPatient,
      }),
    );
  }, [url, selectedPatient, dispatch]);

  const handleEnableMedia = async () => {
    const granted = await requestMediaPermissions();

    if (granted) {
      setShowPermissionModal(false);
    }
  };

  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState !== "visible") return;

      await checkMediaPermissions();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    window.addEventListener("focus", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);

      window.removeEventListener("focus", handleVisibilityChange);
    };
  }, [checkMediaPermissions]);

  const closeVideo = async () => {
    setIsLoading(true);

    const appointmentId = selectedPatient?.appointmentId;

    try {
      await AppointmentService.updateQueueStatus({
        appointmentId,
        patientId: selectedPatient.patientId,
        userId: selectedPatient.userId,
        queueStatus: "N",
      });

      // Safe delete — only removes doc if patient was the original caller
      await deleteCallRecord(appointmentId, selectedPatient.patientId);

      dispatch(DeleteVideoUrl());

      sessionStorage.removeItem("callRoomId");
      sessionStorage.removeItem("selectedAppt");

      dispatch(clearSelectedAppt(null));

      navigate(APP_ROUTES.LOGIN);
    } catch (error) {
      console.error("closeVideo error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const shouldShowPermissionGate = mediaState !== "granted";

  return (
    <div>
      <div className="relative">
        {shouldShowPermissionGate && (
          <div className="w-full h-screen flex items-center justify-center p-5">
            <div className="max-w-lg w-full">
              {mediaState === "checking" && (
                <Flex align="center" justify="center" gap="middle">
                  <Spin size="large" tip="Checking camera and microphone..." />
                </Flex>
              )}

              {(mediaState === "prompt" || mediaState === "requesting") && (
                <div className="text-center">
                  <h2 className="text-xl font-semibold mb-3">
                    Camera & Microphone Access Required
                  </h2>

                  <p className="mb-5">
                    Please allow your camera and microphone so you can join the
                    consultation.
                  </p>

                  <button
                    onClick={handleEnableMedia}
                    disabled={mediaState === "requesting"}
                    className="border-primaryColor border bg-primaryColor rounded-md p-3 text-white"
                  >
                    {mediaState === "requesting"
                      ? "Requesting Access..."
                      : "Enable Camera & Microphone"}
                  </button>
                </div>
              )}

              {(mediaState === "denied" || mediaState === "blocked") && (
                <div className="text-center">
                  <h2 className="text-xl font-semibold mb-3">
                    Camera & Microphone Blocked
                  </h2>

                  <p className="mb-4 text-gray-600">
                    Your browser has blocked camera or microphone access for
                    this site.
                  </p>

                  <Alert
                    type="warning"
                    showIcon
                    message="Allow camera and microphone in your browser/site settings."
                    description="After changing the permission, return to this page and press Retry."
                  />

                  <button
                    onClick={handleEnableMedia}
                    className="mt-5 border-primaryColor border bg-primaryColor rounded-md p-3 text-white"
                  >
                    Retry
                  </button>
                </div>
              )}

              {mediaState === "unsupported" && (
                <div className="text-center">
                  <h2 className="text-xl font-semibold mb-3">
                    Browser Not Supported
                  </h2>

                  <p>
                    Please open this consultation using a browser that supports
                    camera and microphone access and make sure the page is
                    served over HTTPS.
                  </p>
                </div>
              )}

              {mediaState === "not-found" && (
                <div className="text-center">
                  <h2 className="text-xl font-semibold mb-3">
                    Camera or Microphone Not Found
                  </h2>

                  <p>{mediaError}</p>
                </div>
              )}

              {mediaState === "busy" && (
                <div className="text-center">
                  <h2 className="text-xl font-semibold mb-3">
                    Camera or Microphone Busy
                  </h2>

                  <p>{mediaError}</p>

                  <button
                    onClick={handleEnableMedia}
                    className="mt-5 border-primaryColor border bg-primaryColor rounded-md p-3 text-white"
                  >
                    Retry
                  </button>
                </div>
              )}

              {mediaState === "error" && (
                <div className="text-center">
                  <h2 className="text-xl font-semibold mb-3">
                    Unable to Access Camera/Microphone
                  </h2>

                  <p className="mb-4">{mediaError}</p>

                  <button
                    onClick={handleEnableMedia}
                    className="border-primaryColor border bg-primaryColor rounded-md p-3 text-white"
                  >
                    Retry
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {mediaState === "granted" && (
          <>
            {!url && !iframeError && (
              <div className="w-full h-screen flex items-center justify-center">
                <Flex align="center" gap="middle">
                  <Spin size="large" tip="Preparing consultation..." />
                </Flex>
              </div>
            )}

            {iframeError && (
              <div className="w-full h-screen flex items-center justify-center">
                <div className="text-center">
                  <h2 className="text-xl font-semibold mb-3">
                    Unable to Load Consultation
                  </h2>

                  <p className="mb-4">Please try again.</p>

                  <button
                    onClick={() => {
                      setIframeError(false);
                      setUrl("");
                      getLink();
                    }}
                    className="border-primaryColor border bg-primaryColor rounded-md p-3 text-white"
                  >
                    Retry Call
                  </button>
                </div>
              </div>
            )}

            {url && !iframeError && (
              <div id="videoframe" className="h-screen relative">
                {!iframeLoaded && (
                  <div className="absolute inset-0 z-10 bg-white flex items-center justify-center">
                    <Flex align="center" gap="middle">
                      <Spin size="large" tip="Joining consultation..." />
                    </Flex>
                  </div>
                )}

                <iframe
                  id="ccc"
                  title="Video Consultation"
                  src={url}
                  allow="
                    camera;
                    microphone;
                    autoplay;
                    fullscreen;
                    display-capture;
                  "
                  allowFullScreen
                  onLoad={() => {
                    console.log("Video iframe loaded:", url);
                    setIframeLoaded(true);
                  }}
                  onError={(error) => {
                    console.error("Video iframe error:", error);
                    setIframeError(true);
                  }}
                  style={{
                    width: "100%",
                    height: "100vh",
                    border: "none",
                  }}
                />
              </div>
            )}

            <div className="flex items-center justify-between mx-2">
              <button
                onClick={() => setShowModal(true)}
                className="border-primaryColor border bg-primaryColor rounded-md p-2 text-white flex items-center"
              >
                Share Medical Records
              </button>

              <button
                onClick={closeVideo}
                disabled={isLoading}
                className="border-red border bg-red-600 m-3 rounded-md p-2 text-white flex relative text-right items-center hover:bg-brand-hoverRed hover:border-brand-hoverRed disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? "Ending..." : "End Consultation"}
              </button>
            </div>
          </>
        )}

        {showModal && (
          <Sharemedicalrecords
            userId={selectedPatient?.userId}
            onClose={() => setShowModal(false)}
            selectedPatient={selectedPatient}
          />
        )}

        <Modal
          open={showPermissionModal}
          title="Camera & Microphone Permission Required"
          footer={
            <button
              onClick={handleEnableMedia}
              className="border-primaryColor border bg-primaryColor rounded-md p-2 text-white"
            >
              Retry
            </button>
          }
          onCancel={() => setShowPermissionModal(false)}
        >
          <p>Your browser has blocked access to the camera or microphone.</p>

          <p className="mt-3">
            Open the browser's site permissions, change Camera and Microphone
            from <strong>Block</strong> to <strong>Allow</strong>, then return
            here and press Retry.
          </p>
        </Modal>
      </div>
    </div>
  );
};

export default VideoCall;
